import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/surveys';

const TARGET_TYPES = [
  { key: 'Customer', label: 'Müşteri' },
  { key: 'Representative', label: 'Temsilci' },
];
const TARGET_TYPE_LABEL = Object.fromEntries(TARGET_TYPES.map((t) => [t.key, t.label]));

// A1 — Anket yaşam döngüsü durumları
const SURVEY_STATUSES = [
  { key: 'Taslak', label: 'Taslak', tone: 'neutral' },
  { key: 'Yayında', label: 'Yayında', tone: 'ok' },
  { key: 'SüresiDoldu', label: 'Süresi Doldu', tone: 'warn' },
  { key: 'Arşivde', label: 'Arşivde', tone: 'neutral' },
];
const SURVEY_STATUS_LABEL = Object.fromEntries(SURVEY_STATUSES.map((s) => [s.key, s.label]));
const SURVEY_STATUS_TONE = Object.fromEntries(SURVEY_STATUSES.map((s) => [s.key, s.tone]));

// Surveys controller REST/custom'dur (OData değil): list endpoint'i ApiResponse ile
// { items, activeCount, inactiveCount } döner ve $filter/$orderby/$skip/$top desteklemez.
// Bu yüzden useC360List (OData URL kurar) kullanılamaz; veri tek seferde take=500 ile
// çekilip c360 görünümünde client-side filtre/sıralama/sayfalama yapılır.
// Ana kayıt + sorular + cevaplar düzenleme artık tam-sayfa detaya (/surveys/:id) taşındı.
const COLUMN_FILTER_META = {
  targetType: { type: 'select', odataField: 'TargetType', options: TARGET_TYPES.map((t) => ({ value: t.key, label: t.label })) },
  status: { type: 'select', odataField: 'Status', options: SURVEY_STATUSES.map((s) => ({ value: s.key, label: s.label })) },
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapList(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    activeCount: pick(data, 'activeCount', 'ActiveCount') ?? 0,
    inactiveCount: pick(data, 'inactiveCount', 'InactiveCount') ?? 0,
  };
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '-'; }
}

const PAGE_SIZE_DEFAULT = 10;

export function SurveysPage() {
  usePageMeta('Anketler', 'Saha temsilcisine atanan anketler ve soruları');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  // Ham veri (take=500 ile tek seferde çekilir), client-side filtre/sıralama/sayfalama.
  const [allRows, setAllRows] = useState([]);
  const [counts, setCounts] = useState({ activeCount: 0, inactiveCount: 0 });
  // Yusuf Fix — LİSTE AÇILIŞINDA "Kayıt bulunamadı" YANIP SÖNMESİN: veri mount'taki effect'te
  // çekiliyor, yani ilk boyama fetch'ten ÖNCE olur. loading false başlayınca tablo o ilk karede
  // boş-durum metnini gösteriyor, veri gelince listeye dönüyordu — kullanıcı "önce kayıt yok dedi,
  // sonra liste geldi" diye okuyor. Yükleme durumu ilk kareden itibaren AÇIK başlar.
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [currentPage, setCurrentPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [selected, setSelected] = useState(null);

  const [deleteSurveyRow, setDeleteSurveyRow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapList(payload);
      setAllRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ activeCount: data.activeCount, inactiveCount: data.inactiveCount });
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Client-side filtre + sıralama ─────────────────────────────────────────
  const filteredSorted = useMemo(() => {
    const needle = filterText.trim().toLocaleLowerCase('tr-TR');
    const targetFilter = columnFilters.targetType?.value ?? '';
    const statusFilter = columnFilters.status?.value ?? '';
    const match = (row) => {
      if (needle) {
        const fields = [
          pick(row, 'textId', 'TextId'),
          pick(row, 'title', 'Title'),
          pick(row, 'description', 'Description'),
        ];
        if (!fields.some((v) => v && String(v).toLocaleLowerCase('tr-TR').includes(needle))) return false;
      }
      if (targetFilter && (pick(row, 'targetType', 'TargetType') || '') !== targetFilter) return false;
      if (statusFilter !== '') {
        const st = pick(row, 'status', 'Status') || (pick(row, 'isActive', 'IsActive') ? 'Yayında' : 'Taslak');
        if (st !== statusFilter) return false;
      }
      return true;
    };
    const filtered = allRows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'TargetType': return pick(row, 'targetType', 'TargetType') || '';
        case 'QuestionCount': return Number(pick(row, 'questionCount', 'QuestionCount')) || 0;
        case 'TextId': return pick(row, 'textId', 'TextId') || '';
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [allRows, filterText, columnFilters, sortField, sortDir]);

  const totalCount = filteredSorted.length;

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, currentPage, pageSize]);

  const applyFilterText = (text) => { setFilterText(text); setCurrentPage(1); };
  const setColumnFilter = (columnId, next) => {
    setColumnFilters((prev) => ({ ...prev, [columnId]: next }));
    setCurrentPage(1);
  };
  const clearColumnFilter = (columnId) => {
    setColumnFilters((prev) => { const copy = { ...prev }; delete copy[columnId]; return copy; });
    setCurrentPage(1);
  };
  const applySort = (odataField, dir) => {
    setCurrentPage(1);
    setSortField(odataField || 'UpdatedDate');
    setSortDir(dir || 'asc');
  };

  // ── Navigasyon (tam-sayfa detay) ───────────────────────────────────────────
  const openCreateSurvey = () => navigate('/surveys/new');
  const openDetail = (row) => navigate(`/surveys/${pick(row, 'id', 'Id')}`, { state: { row } });
  const openEditRow = (row) => navigate(`/surveys/${pick(row, 'id', 'Id')}?edit=1`, { state: { row } });

  // ── Silme ──────────────────────────────────────────────────────────────────
  const submitDeleteSurvey = async () => {
    if (!deleteSurveyRow) return;
    const id = pick(deleteSurveyRow, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Anket silindi (sorular cascade ile silindi).');
      setDeleteSurveyRow(null);
      setSelected(null);
      await fetchData();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    } finally {
      setDeleteBusy(false);
    }
  };

  // ── c360 grid ──────────────────────────────────────────────────────────────

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('title', {
      header: 'Başlık', enableColumnFilter: false,
      cell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700 }}>{pick(row.original, 'title', 'Title') || '-'}</span>
          {pick(row.original, 'textId', 'TextId') && (
            <span className="c360-erp-chip" style={{ marginTop: 2 }}>{pick(row.original, 'textId', 'TextId')}</span>
          )}
        </div>
      ),
    }),
    columnHelper.accessor('targetType', {
      header: 'Hedef', meta: { filterType: 'select' },
      cell: (info) => { const v = info.getValue(); return TARGET_TYPE_LABEL[v] || v || '-'; },
    }),
    columnHelper.accessor('questionCount', {
      header: 'Soru', enableColumnFilter: false,
      cell: (info) => info.getValue() ?? 0,
    }),
    columnHelper.display({
      id: 'period', header: 'Dönem', enableSorting: false, enableColumnFilter: false,
      cell: ({ row }) => `${formatDate(pick(row.original, 'startDate', 'StartDate'))} – ${formatDate(pick(row.original, 'endDate', 'EndDate'))}`,
    }),
    columnHelper.accessor('status', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: ({ row }) => {
        const st = pick(row.original, 'status', 'Status')
          || (pick(row.original, 'isActive', 'IsActive') ? 'Yayında' : 'Taslak');
        return <StatusBadge tone={SURVEY_STATUS_TONE[st] || 'neutral'}>{SURVEY_STATUS_LABEL[st] || st}</StatusBadge>;
      },
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteSurveyRow(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: pagedRows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(pick(row, 'id', 'Id')),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Kod' },
        { field: 'title', headerName: 'Başlık' },
        { headerName: 'Hedef', valueGetter: ({ row }) => TARGET_TYPE_LABEL[pick(row, 'targetType', 'TargetType')] || pick(row, 'targetType', 'TargetType') || '' },
        { headerName: 'Anket Tipi', valueGetter: ({ row }) => pick(row, 'surveyType', 'SurveyType') || '' },
        { headerName: 'Soru', valueGetter: ({ row }) => pick(row, 'questionCount', 'QuestionCount') ?? 0 },
        { headerName: 'Başlangıç', valueGetter: ({ row }) => formatDate(pick(row, 'startDate', 'StartDate')) },
        { headerName: 'Bitiş', valueGetter: ({ row }) => formatDate(pick(row, 'endDate', 'EndDate')) },
        { headerName: 'Durum', valueGetter: ({ row }) => {
          const st = pick(row, 'status', 'Status') || (pick(row, 'isActive', 'IsActive') ? 'Yayında' : 'Taslak');
          return SURVEY_STATUS_LABEL[st] || st;
        } },
      ];
      if (filteredSorted.length === 0) { showToast('error', 'Dışa aktarılacak veri yok'); return; }
      const dateStr = new Date().toISOString().slice(0, 10);
      exportToExcel(filteredSorted, cols, `anketler_${dateStr}`);
      showToast('success', `${filteredSorted.length} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Başlık / kod / açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={pagedRows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Anketler"
        subtitle="Saha temsilcisine atanan anketler. Her anket çoklu soru içerir; mobile master delta ile çeker, cevaplar push ile gelir."
        actions={(
          <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
            <StatusBadge tone="ok">Aktif: {counts.activeCount}</StatusBadge>
            <StatusBadge tone="neutral">Pasif: {counts.inactiveCount}</StatusBadge>
            {canAdd && <button style={newRecordButtonStyle} onClick={openCreateSurvey}>+ Yeni Anket</button>}
          </span>
        )}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      {deleteSurveyRow && (
        <div style={s.overlay} onClick={() => setDeleteSurveyRow(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Anketi Sil</h3>
              <button type="button" style={s.closeBtn} onClick={() => setDeleteSurveyRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>{pick(deleteSurveyRow, 'title', 'Title')}</strong> anketini silmek istediğinize emin misiniz?
              </p>
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
                Anket ve tüm soruları (cascade) silinir. Mevcut cevaplar tabloda dangling kalabilir.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }}
                onClick={submitDeleteSurvey}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
              <button type="button" style={s.btnSecondary} onClick={() => setDeleteSurveyRow(null)} disabled={deleteBusy}>
                Vazgeç
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default SurveysPage;
